import React, { useEffect, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { Bold, Italic, List, ListOrdered, Link2, Underline as UnderlineIcon, Mic, Video, ImageIcon, Heading1, Heading2, Heading3, Strikethrough, Code, Quote } from "lucide-react";
import { cn } from "../../../lib/utils";
import { RecordingData } from "../../../hooks/useMediaRecorder";
import { RecordingDialog } from "../../recording/RecordingDialog";
import { RecordingPlayer } from "../../recording/RecordingPlayer";
import { deleteRecording } from "../../../api/recordings";
import { deleteRecordingFromPrescription } from "../../../api/prescriptionRecordings";
import { ImageData as ProcessImageData, deleteImage as deleteProcessImage } from "../../../api/processImages";
import { deleteImageFromPrescription } from "../../../api/prescriptionImages";
import useRetryFetch from "../../../hooks/useRetryFetch";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { ImageUploadDialog } from "../../images/ImageUploadDialog";
import { ImageViewer } from "../../images/ImageViewer";

export interface PaneRecordings {
  audio?: RecordingData;
  video?: RecordingData;
  image?: ProcessImageData;
}

interface MinimalEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** When "extended", adds headings, strikethrough, code, blockquote to the toolbar. Default "minimal". */
  toolbarMode?: "minimal" | "extended";
  recordings?: PaneRecordings;
  onRecordingsChange?: (recordings: PaneRecordings) => void;
  recordingContext?: 'pane' | 'prologue' | 'epilogue' | 'additionalInfo';
  processId?: string; // Process ID for R2 upload (or prescriptionId for additionalInfo context)
  activityId?: string; // Activity ID for pane/additionalInfo context
}

export function MinimalEditor({
  value,
  onChange,
  placeholder = "Add context, notes, or links...",
  toolbarMode = "minimal",
  recordings,
  onRecordingsChange,
  recordingContext = 'pane',
  processId,
  activityId,
}: MinimalEditorProps) {
  const [audioRecordingOpen, setAudioRecordingOpen] = useState(false);
  const [videoRecordingOpen, setVideoRecordingOpen] = useState(false);
  const [imageUploadOpen, setImageUploadOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const retryFetch = useRetryFetch();
  const { t } = useTranslation();

  // For image upload/delete: activityId for pane/additionalInfo, or 'prologue'/'epilogue'
  const imageContextActivityId =
    recordingContext === "pane"
      ? activityId
      : recordingContext === "prologue"
        ? "prologue"
        : recordingContext === "epilogue"
          ? "epilogue"
          : activityId;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: "text-primary underline cursor-pointer",
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      Underline,
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none min-h-[120px] p-3",
      },
    },
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Update content when value changes externally
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "");
    }
  }, [value, editor]);

  if (!editor) {
    return null;
  }

  const addLink = () => {
    const url = window.prompt("Enter URL:");
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  const handleAudioComplete = (data: RecordingData) => {
    if (onRecordingsChange) {
      onRecordingsChange({
        ...recordings,
        audio: data,
      });
    }
    setAudioRecordingOpen(false);
  };

  const handleVideoComplete = (data: RecordingData) => {
    if (onRecordingsChange) {
      onRecordingsChange({
        ...recordings,
        video: data,
      });
    }
    setVideoRecordingOpen(false);
  };

  const handleAudioDelete = async () => {
    if (!onRecordingsChange) return;

    // If recording has a key (uploaded to R2), delete from cloud
    if (recordings?.audio?.key && processId) {
      setIsDeleting(true);
      try {
        if (recordingContext === 'additionalInfo') {
          // Use prescription-specific API
          await deleteRecordingFromPrescription(
            processId, // This is actually prescriptionId for additionalInfo
            'audio',
            activityId || '',
            retryFetch
          );
        } else {
          // Use process-specific API
          await deleteRecording(
            processId,
            'audio',
            recordingContext as 'pane' | 'prologue' | 'epilogue',
            recordingContext === 'pane' ? activityId : undefined,
            retryFetch
          );
        }
        toast.success('Audio recording deleted');
      } catch (error) {
        console.error('Failed to delete audio from cloud:', error);
        toast.error('Failed to delete audio recording');
        setIsDeleting(false);
        return; // Don't remove from UI if cloud delete failed
      }
      setIsDeleting(false);
    }

    // Remove from local state
    const { audio, ...rest } = recordings || {};
    onRecordingsChange(rest as PaneRecordings);
  };

  const handleVideoDelete = async () => {
    if (!onRecordingsChange) return;

    // If recording has a key (uploaded to R2), delete from cloud
    if (recordings?.video?.key && processId) {
      setIsDeleting(true);
      try {
        if (recordingContext === 'additionalInfo') {
          // Use prescription-specific API
          await deleteRecordingFromPrescription(
            processId, // This is actually prescriptionId for additionalInfo
            'video',
            activityId || '',
            retryFetch
          );
        } else {
          // Use process-specific API
          await deleteRecording(
            processId,
            'video',
            recordingContext as 'pane' | 'prologue' | 'epilogue',
            recordingContext === 'pane' ? activityId : undefined,
            retryFetch
          );
        }
        toast.success('Video recording deleted');
      } catch (error) {
        console.error('Failed to delete video from cloud:', error);
        toast.error('Failed to delete video recording');
        setIsDeleting(false);
        return; // Don't remove from UI if cloud delete failed
      }
      setIsDeleting(false);
    }

    // Remove from local state
    const { video, ...rest } = recordings || {};
    onRecordingsChange(rest as PaneRecordings);
  };

  const handleImageComplete = (data: ProcessImageData) => {
    if (onRecordingsChange) {
      onRecordingsChange({
        ...recordings,
        image: data,
      });
    }
    setImageUploadOpen(false);
  };

  const handleImageDelete = async () => {
    if (!onRecordingsChange || !recordings?.image) return;
    if (recordings.image.key && processId && imageContextActivityId) {
      setIsDeleting(true);
      try {
        if (recordingContext === "additionalInfo") {
          await deleteImageFromPrescription(
            processId,
            imageContextActivityId,
            retryFetch
          );
        } else {
          await deleteProcessImage(
            processId,
            imageContextActivityId,
            retryFetch
          );
        }
        toast.success("Image deleted");
      } catch (error) {
        console.error("Failed to delete image:", error);
        toast.error("Failed to delete image");
        setIsDeleting(false);
        return;
      }
      setIsDeleting(false);
    }
    const { image: _img, ...rest } = recordings;
    onRecordingsChange(rest as PaneRecordings);
  };

  return (
    <div className="border rounded-md overflow-hidden">
      {/* Minimal Toolbar - wraps to two rows when needed */}
      <div className="flex flex-wrap items-center gap-1 p-1.5 border-b bg-muted/30">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={cn(
            "p-1.5 rounded hover:bg-muted transition-colors",
            editor.isActive("bold") ? "bg-muted text-primary" : ""
          )}
          title="Bold"
        >
          <Bold className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={cn(
            "p-1.5 rounded hover:bg-muted transition-colors",
            editor.isActive("italic") ? "bg-muted text-primary" : ""
          )}
          title="Italic"
        >
          <Italic className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={cn(
            "p-1.5 rounded hover:bg-muted transition-colors",
            editor.isActive("underline") ? "bg-muted text-primary" : ""
          )}
          title="Underline"
        >
          <UnderlineIcon className="h-4 w-4" />
        </button>
        <div className="w-px h-5 bg-border mx-1" />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={cn(
            "p-1.5 rounded hover:bg-muted transition-colors",
            editor.isActive("bulletList") ? "bg-muted text-primary" : ""
          )}
          title="Bullet List"
        >
          <List className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={cn(
            "p-1.5 rounded hover:bg-muted transition-colors",
            editor.isActive("orderedList") ? "bg-muted text-primary" : ""
          )}
          title="Numbered List"
        >
          <ListOrdered className="h-4 w-4" />
        </button>
        <div className="w-px h-5 bg-border mx-1" />
        <button
          type="button"
          onClick={addLink}
          className={cn(
            "p-1.5 rounded hover:bg-muted transition-colors",
            editor.isActive("link") ? "bg-muted text-primary" : ""
          )}
          title="Add Link"
        >
          <Link2 className="h-4 w-4" />
        </button>

        {/* Extended toolbar - headings, strikethrough, code, blockquote */}
        {toolbarMode === "extended" && (
          <>
            <div className="w-px h-5 bg-border mx-1" />
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              className={cn(
                "p-1.5 rounded hover:bg-muted transition-colors",
                editor.isActive("heading", { level: 1 }) ? "bg-muted text-primary" : ""
              )}
              title="Heading 1"
            >
              <Heading1 className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              className={cn(
                "p-1.5 rounded hover:bg-muted transition-colors",
                editor.isActive("heading", { level: 2 }) ? "bg-muted text-primary" : ""
              )}
              title="Heading 2"
            >
              <Heading2 className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              className={cn(
                "p-1.5 rounded hover:bg-muted transition-colors",
                editor.isActive("heading", { level: 3 }) ? "bg-muted text-primary" : ""
              )}
              title="Heading 3"
            >
              <Heading3 className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleStrike().run()}
              className={cn(
                "p-1.5 rounded hover:bg-muted transition-colors",
                editor.isActive("strike") ? "bg-muted text-primary" : ""
              )}
              title="Strikethrough"
            >
              <Strikethrough className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleCode().run()}
              className={cn(
                "p-1.5 rounded hover:bg-muted transition-colors",
                editor.isActive("code") ? "bg-muted text-primary" : ""
              )}
              title="Inline Code"
            >
              <Code className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              className={cn(
                "p-1.5 rounded hover:bg-muted transition-colors",
                editor.isActive("blockquote") ? "bg-muted text-primary" : ""
              )}
              title="Blockquote"
            >
              <Quote className="h-4 w-4" />
            </button>
          </>
        )}

        {/* Recording buttons - only show if handlers are provided */}
        {onRecordingsChange && (
          <>
            <div className="w-px h-5 bg-border mx-1" />
            
            {/* Audio Recording Button */}
            <button
              type="button"
              onClick={() => setAudioRecordingOpen(true)}
              className={cn(
                "p-1.5 rounded hover:bg-muted transition-colors",
                recordings?.audio ? "bg-primary/10 text-primary" : ""
              )}
              title="Record Audio"
            >
              <Mic className="h-4 w-4" />
            </button>

            {/* Video Recording Button */}
            <button
              type="button"
              onClick={() => setVideoRecordingOpen(true)}
              className={cn(
                "p-1.5 rounded hover:bg-muted transition-colors",
                recordings?.video ? "bg-purple-100 text-purple-600" : ""
              )}
              title="Record Video"
            >
              <Video className="h-4 w-4" />
            </button>

            {/* Image Note Button */}
            <button
              type="button"
              onClick={() => setImageUploadOpen(true)}
              className={cn(
                "p-1.5 rounded hover:bg-muted transition-colors",
                recordings?.image ? "bg-green-100 text-green-600" : ""
              )}
              title={t("add_image", "Add Image")}
            >
              <ImageIcon className="h-4 w-4" />
            </button>
          </>
        )}
      </div>

      {/* Editor Content */}
      <EditorContent editor={editor} />

      {/* Recording Players and Image - show below editor */}
      {(recordings?.audio || recordings?.video || recordings?.image) && (
        <div className="p-2 border-t bg-muted/10 space-y-1.5">
          {recordings?.audio && (
            <RecordingPlayer
              recording={recordings.audio}
              type="audio"
              onDelete={handleAudioDelete}
              compact
            />
          )}
          {recordings?.video && (
            <RecordingPlayer
              recording={recordings.video}
              type="video"
              onDelete={handleVideoDelete}
              compact
            />
          )}
          {recordings?.image && (
            <ImageViewer
              image={recordings.image}
              onDelete={handleImageDelete}
              compact
            />
          )}
        </div>
      )}

      {/* Recording Dialogs */}
      {onRecordingsChange && (
        <>
          <RecordingDialog
            open={audioRecordingOpen}
            onOpenChange={setAudioRecordingOpen}
            type="audio"
            onComplete={handleAudioComplete}
            mode="record"
            context={recordingContext}
            processId={processId}
            activityId={activityId}
          />
          <RecordingDialog
            open={videoRecordingOpen}
            onOpenChange={setVideoRecordingOpen}
            type="video"
            onComplete={handleVideoComplete}
            mode="record"
            context={recordingContext}
            processId={processId}
            activityId={activityId}
          />
          <ImageUploadDialog
            open={imageUploadOpen}
            onOpenChange={setImageUploadOpen}
            onComplete={handleImageComplete}
            context={recordingContext}
            processId={recordingContext !== "additionalInfo" ? processId : undefined}
            prescriptionId={recordingContext === "additionalInfo" ? processId : undefined}
            activityId={imageContextActivityId}
          />
        </>
      )}
    </div>
  );
}
